import { useCallback, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { buildAgentV2ReferenceViewerSession } from '@/agent-v2/references/build-agent-v2-reference-viewer-session'
import { canOpenAgentV2Reference, getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import { openStandaloneViewerTab } from '@/standalone-reference-viewer/utils/open-standalone-viewer-tab'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useSourceOpensInChatPanel } from '@/drafting/workspace/hooks/useSourceOpensInChatPanel'
import { useOpenAgentV2ReferenceViewer } from '@/agent-v2/hooks/useOpenAgentV2ReferenceViewer'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import type { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { AgentV2ReferenceOpenContext } from '@/agent-v2/references/agent-v2-reference-open-context'

/**
 * Whether a chat reference opened from this view goes to the in-app content viewer rather than a
 * new browser tab: true in the normal chat view, and in the draft workspace when its chat panel
 * can show the source. A host override (the Outlook task pane) opens sources its own way.
 * Pass it to canOpenAgentV2Reference so a source only the panel can show is not offered here.
 */
export function useAgentV2ReferenceOpensInPanel(): boolean {
  const openOverride = useContext(AgentV2ReferenceOpenContext)
  const { draftArtifactVersionId } = useDraftWorkspace()
  const sourceOpensInChatPanel = useSourceOpensInChatPanel()

  return openOverride === null && (draftArtifactVersionId == null || sourceOpensInChatPanel)
}

/**
 * Opens a source referenced in a chat, returning where it opened so
 * callers can tell the in-panel viewer apart from a new browser tab (e.g.
 * citator panel analytics must not fire for a tab that shows the source view).
 * - In the normal chat view the reference opens in the content viewer
 * - In the draft workspace view it opens in the chat panel's source tab when the panel can show it, and in a new browser tab when it cannot (see useSourceOpensInChatPanel)
 */
export type OpenAgentV2ReferenceResult = 'panel' | 'tab' | false
export function useOpenAgentV2Reference() {
  const openOverride = useContext(AgentV2ReferenceOpenContext)
  const { matterId } = useParams()
  const opensInPanel = useAgentV2ReferenceOpensInPanel()
  const openReferenceViewer = useOpenAgentV2ReferenceViewer()

  return useCallback(
    (
      reference: AgentV2Reference | undefined,
      turnId?: string,
      options?: { defaultTab?: CaseLawReferenceTab; defaultOpenSection?: RiaCitatorSection }
    ): OpenAgentV2ReferenceResult => {
      if (!reference) return false
      const externalUrl = getAgentV2ExternalReferenceUrl(reference)
      if (externalUrl) return openStandaloneViewerTab(externalUrl) && 'tab'
      if (reference.source_type === 'administrative_decision' && !canOpenAgentV2Reference(reference, { opensInPanel })) return false

      if (openOverride) return openOverride(reference, turnId, options)

      if (opensInPanel) {
        return openReferenceViewer(reference, turnId, options) && 'panel'
      }

      // Opening in a browser tab carries both the tab and citator-section intent into the URL.
      const session = buildAgentV2ReferenceViewerSession(reference, turnId)
      const viewerUrl = resolveStandaloneViewerUrlFromSession(
        session,
        {
          conversationId: reference.conversation_id,
          messageId: reference.turn_id,
          matterId,
        },
        { defaultTab: options?.defaultTab, defaultOpenSection: options?.defaultOpenSection }
      )
      if (!viewerUrl) return false

      return openStandaloneViewerTab(viewerUrl) && 'tab'
    },
    [matterId, openOverride, openReferenceViewer, opensInPanel]
  )
}
